import { defineStore } from 'pinia';
import * as paymentsApi from '@/js/api/payments.js';

/**
 * Holds the in-flight PaymentTransactionRequest/Response pair.
 *
 * Persisted so that a Keycloak step-up authentication round-trip
 * (full page redirect) can restore the pending payment and resume
 * automatically when the user returns with acr=gold.
 */
export const usePaymentTransactionStore = defineStore('paymentTransaction', {
  state: () => ({
    currentRequest: null,
    currentResponse: null,
    lastError: null,
    isSubmitting: false,
    /**
     * Pending payment intent created by a trigger (campaign contribution,
     * ad top-up, listing purchase, cart checkout, payout...).
     * Shape: { reference, request: PaymentTransactionRequest, display }
     * Consumed by the /pay/:reference page on mount, then cleared.
     */
    draft: null,
  }),
  persist: true,
  actions: {
    setRequest(request) {
      this.currentRequest = request;
    },

    setDraft(draft) {
      this.draft = draft;
    },

    clearDraft() {
      this.draft = null;
    },

    clear() {
      this.currentRequest = null;
      this.currentResponse = null;
      this.lastError = null;
      this.isSubmitting = false;
      this.draft = null;
    },

    /**
     * POST /payments/initiate (PAY_IN) or /payments/payout/initiate
     * depending on the request's paymentType.
     */
    async submit(request) {
      const req = request || this.currentRequest;
      if (!req) throw new Error('No payment request to submit');
      this.setRequest(req);
      this.isSubmitting = true;
      this.lastError = null;
      try {
        const isPayOut = String(req.paymentType || '').toUpperCase() === 'PAY_OUT';
        const response = isPayOut ? await paymentsApi.initiatePayout(req) : await paymentsApi.initiatePayment(req);
        this.currentResponse = response;
        return response;
      } catch (error) {
        this.lastError = error?.response?.data?.message || error?.message || 'Payment initiation failed';
        throw error;
      } finally {
        this.isSubmitting = false;
      }
    },

    async refreshStatus(transactionReference) {
      const ref = transactionReference || this.currentResponse?.transactionReference;
      if (!ref) return null;
      const response = await paymentsApi.queryProviderStatus(ref);
      this.currentResponse = response;
      return response;
    },

    async getStatus(transactionReference) {
      const ref = transactionReference || this.currentResponse?.transactionReference;
      if (!ref) return null;
      return paymentsApi.getPaymentStatus(ref);
    },
  },
});
